import React, { useEffect, useRef } from "react";
import { clearGlobalError } from "../../../store/reducers/errorSlice";
import { getStore } from "../../../store/storeRegistry";

const openModals: symbol[] = [];

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: "sm" | "md" | "lg";
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  size = "md",
}) => {
  // MED-1476: the global error modal renders above every dialog and used to
  // survive until dismissed by hand, so a stale error stayed on top of the
  // next dialog the user opened. Opening a dialog supersedes it. The store is
  // read from the registry rather than `useDispatch` so this stays a plain
  // presentational component outside a <Provider>.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      getStore()?.dispatch(clearGlobalError());
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  // Every open modal, deepest last. MED-1543: a dialog can now open a
  // confirmation on top of itself, and both listen for Escape on `document`.
  // Without this, one Escape reached both — the confirmation closed and the
  // dialog underneath immediately reopened it. Only the top-most reacts.
  const idRef = useRef<symbol | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    const id = Symbol("modal");
    idRef.current = id;
    openModals.push(id);
    // Prevent scrolling on the body while any modal is open.
    document.body.style.overflow = "hidden";

    return () => {
      const index = openModals.indexOf(id);
      if (index !== -1) openModals.splice(index, 1);
      // Only the last one out restores the page's scrolling — closing a
      // confirmation used to hand it back while its parent was still open.
      if (openModals.length === 0) document.body.style.overflow = "auto";
    };
    // Deliberately keyed on `isOpen` alone: the order of this list is the
    // order the modals opened in, and it must not be rewritten by a parent
    // re-rendering with a new `onClose`.
  }, [isOpen]);

  // Close modal when Escape key is pressed
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (openModals[openModals.length - 1] !== idRef.current) return;
      // A control with an open popup owns Escape: it closes its own list
      // rather than the dialog around it.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[aria-expanded="true"][aria-haspopup]')) return;
      onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        ></div>

        {/* Center modal */}
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal panel */}
        <div
          role="dialog"
          aria-modal="true"
          className={`inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-2xl ${
            size === "sm"
              ? "max-w-sm"
              : size === "md"
                ? "max-w-2xl"
                : "max-w-4xl"
          }`}
          onClick={e => e.stopPropagation()}
        >
          {title && (
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
